// weitereEintraegeFelder.tsx — Felder und Regeln einer WEITEREN Anschrift bzw.
// Bankverbindung. Beide Anlagen (Privat, Firma) hatten sie bis 09/2026 je
// einmal inline; jetzt stehen sie hier und laufen durch die EintragsListe.

import * as React from "react";
import { FieldRow, LabeledInput, LabeledSelect } from "./formPrimitives";
import { CountrySelect } from "./widgets/CountrySelect";
import { AddressAutocomplete } from "./widgets/AddressAutocomplete";
import { KartenKnopf } from "./widgets/KartenKnopf";
import { IbanInput } from "./widgets/IbanInput";
import { BicInput } from "./widgets/BicInput";
import type { BankTreffer } from "./widgets/bankleitzahlen";
import { plzPruefen, plzSperrtSpeichern } from "./validation/laenderprofil";
import { validateBic, validateIban } from "./validation";
import { ADRESS_TYPEN } from "../../data/adresstypen";

export type WeitereAnschrift = {
  addressType?: string;
  addressDescription?: string;
  street?: string;
  postalCode?: string;
  city?: string;
  cityArea?: string;
  country?: string;
  [k: string]: unknown;
};

export type WeitereBank = {
  accountHolder?: string;
  iban?: string;
  bic?: string;
  bankname?: string;
  bankbezeichnung?: string;
  [k: string]: unknown;
};

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const leer = (v: unknown): boolean => !String(v ?? "").trim();

export const neueAnschrift = (): WeitereAnschrift => ({
  street: "",
  postalCode: "",
  city: "",
  cityArea: "",
  // Wie die Hauptanschrift: „Deutschland" steht schon drin (User 26.09.2026).
  // Zählt beim Leer-Test nicht mit, siehe anschriftIstLeer.
  country: "Deutschland",
});

/** Das Land allein macht noch keine Anschrift. */
export const anschriftIstLeer = (a: WeitereAnschrift): boolean =>
  [a.street, a.postalCode, a.city, a.cityArea].every(leer);

export const anschriftPruefen = (a: WeitereAnschrift): string | null =>
  plzSperrtSpeichern(str(a.postalCode), str(a.country)) ? "Bitte die Postleitzahl prüfen." : null;

export const neueBank = (): WeitereBank => ({
  accountHolder: "",
  iban: "",
  bic: "",
  bankname: "",
  bankbezeichnung: "",
});

export const bankIstLeer = (b: WeitereBank): boolean => leer(b.iban);

export const bankPruefen = (b: WeitereBank): string | null => {
  if (!validateIban(str(b.iban)).valid) return "Die IBAN ist ungültig.";
  if (!validateBic(str(b.bic)).valid) return "Die BIC ist ungültig.";
  return null;
};

/**
 * BIC und Institut aus der erkannten Bankleitzahl — nur in LEERE Felder: eine
 * bewusst abweichende Eingabe (etwa nach einer Bankfusion) bleibt stehen.
 */
export const bankTrefferPatch = (b: WeitereBank, bank: BankTreffer): Partial<WeitereBank> => {
  const patch: Partial<WeitereBank> = {};
  if (bank.bic && leer(b.bic)) patch.bic = bank.bic;
  if (leer(b.bankname)) patch.bankname = bank.bankName;
  return patch;
};

export function WeitereAnschriftFelder({
  wert,
  onChange,
  schluessel,
  kopf,
}: {
  wert: WeitereAnschrift;
  onChange: (patch: Partial<WeitereAnschrift>) => void;
  /** Teil der Feldnamen (`addr-street-${schluessel}` …). */
  schluessel: string;
  /**
   * Privat: „Art der Anschrift" plus optionaler Name (z. B. Ferienimmobilie
   * „Hanselhof", T-358); Firma: nur die freie „Bezeichnung".
   */
  kopf: "art" | "bezeichnung";
}) {
  const land = str(wert.country);
  const plz = plzPruefen(str(wert.postalCode), land);
  // Auch die weitere Anschrift braucht ihr Land — sonst gibt es nichts, wogegen
  // die PLZ geprüft werden könnte. Ohne autoDefault: Das Land steht im
  // Payload-Filter, ein automatisches „Deutschland" machte aus jedem leeren
  // Block eine echte Anschrift.
  const laenderwahl = (
    <CountrySelect value={land} onChange={(v) => onChange({ country: v })} autoDefault={false} />
  );
  return (
    <>
      {kopf === "art" ? (
        <>
          <FieldRow columns={2}>
            <LabeledSelect
              label="Art der Anschrift"
              name={`addr-type-${schluessel}`}
              value={str(wert.addressType)}
              onChange={(v) => onChange({ addressType: v })}
              options={ADRESS_TYPEN.map((o) => ({ value: o.value, label: o.label }))}
              placeholder="Nicht erfasst"
            />
            {laenderwahl}
          </FieldRow>
          <FieldRow columns={2}>
            {/* Eigener Name der Anschrift. Eine automatisch vergebene
                Art-Beschriftung filtert schon das Laden heraus
                (contactToFormState), sie steht hier also nicht als Name. */}
            <LabeledInput
              label="Bezeichnung"
              name={`addr-desc-${schluessel}`}
              value={str(wert.addressDescription)}
              onChange={(v) => onChange({ addressDescription: v })}
              placeholder="z. B. Hanselhof"
            />
            <div />
          </FieldRow>
        </>
      ) : (
        <>
          <FieldRow columns={2}>
            <LabeledInput
              label="Bezeichnung"
              name={`addr-desc-${schluessel}`}
              value={str(wert.addressDescription)}
              onChange={(v) => onChange({ addressDescription: v })}
              placeholder="z. B. Postanschrift"
            />
          </FieldRow>
          <FieldRow columns={2}>{laenderwahl}</FieldRow>
        </>
      )}
      <FieldRow columns={2}>
        <AddressAutocomplete
          label="Straße und Hausnummer"
          name={`addr-street-${schluessel}`}
          value={str(wert.street)}
          onChange={(v) => onChange({ street: v })}
          land={land}
          // Karten-Pin wie an der Hauptanschrift (T-326).
          rightSlot={
            <KartenKnopf
              anschrift={{ strasse: str(wert.street), plz: str(wert.postalCode), ort: str(wert.city), land }}
            />
          }
          onResolved={(r) =>
            onChange({ street: r.street, postalCode: r.postalcode, city: r.city, cityArea: r.cityArea })
          }
        />
        <LabeledInput
          label="Postleitzahl"
          name={`addr-plz-${schluessel}`}
          value={str(wert.postalCode)}
          onChange={(v) => onChange({ postalCode: v })}
          invalid={!plz.ok}
          errorText={plz.meldung}
          placeholder={plzPruefen("", land).beispiel}
        />
      </FieldRow>
      <FieldRow columns={2}>
        <LabeledInput
          label="Ort"
          name={`addr-city-${schluessel}`}
          value={str(wert.city)}
          onChange={(v) => onChange({ city: v })}
        />
        <LabeledInput
          label="Ortsteil"
          name={`addr-area-${schluessel}`}
          value={str(wert.cityArea)}
          onChange={(v) => onChange({ cityArea: v })}
        />
      </FieldRow>
    </>
  );
}

export function WeitereBankFelder({
  wert,
  onChange,
  schluessel,
  kontoinhaberVorschlag = "",
}: {
  wert: WeitereBank;
  onChange: (patch: Partial<WeitereBank>) => void;
  schluessel: string;
  /**
   * Name des Kontakts in Kontoinhaber-Schreibweise. Steht im Feld, solange
   * niemand etwas anderes eingetragen hat, und wird beim Speichern übernommen
   * (Anlage-Payloads). Ein leerer Eintrag folgt so jeder Namensänderung.
   */
  kontoinhaberVorschlag?: string;
}) {
  return (
    <>
      <FieldRow columns={2}>
        <LabeledInput
          label="Kontoinhaber"
          name={`bank-holder-${schluessel}`}
          value={str(wert.accountHolder) || kontoinhaberVorschlag}
          onChange={(v) => onChange({ accountHolder: v })}
        />
        <IbanInput
          label="IBAN"
          name={`bank-iban-${schluessel}`}
          value={str(wert.iban)}
          onChange={(v) => onChange({ iban: v })}
          onBankErkannt={(bank) => onChange(bankTrefferPatch(wert, bank))}
        />
      </FieldRow>
      <FieldRow columns={2}>
        <BicInput
          label="BIC"
          name={`bank-bic-${schluessel}`}
          value={str(wert.bic)}
          onChange={(v) => onChange({ bic: v })}
        />
        <LabeledInput
          label="Kreditinstitut"
          name={`bank-name-${schluessel}`}
          value={str(wert.bankname)}
          onChange={(v) => onChange({ bankname: v })}
        />
      </FieldRow>
      {/* Halbe Breite wie bei der ersten Bank — die Bezeichnung ist ein Stichwort. */}
      <FieldRow columns={2}>
        <LabeledInput
          label="Bezeichnung"
          name={`bank-bez-${schluessel}`}
          value={str(wert.bankbezeichnung)}
          onChange={(v) => onChange({ bankbezeichnung: v })}
          placeholder="z. B. Hauptkonto"
        />
        <div />
      </FieldRow>
    </>
  );
}
